import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, Mic, MicOff, Loader2, Coins, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ChatPanel({ analysisId, availableLlms }) {
  const { t } = useLanguage();
  const { token, user, refreshUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [messagesUsed, setMessagesUsed] = useState(0);
  const [messagesRemaining, setMessagesRemaining] = useState(5);
  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [selectedLlm, setSelectedLlm] = useState('');
  const messagesEndRef = useRef(null);

  // Pick first available LLM
  useEffect(() => {
    if (availableLlms && !selectedLlm) {
      const first = Object.entries(availableLlms).find(([, info]) => info.available);
      if (first) setSelectedLlm(first[0]);
    }
  }, [availableLlms, selectedLlm]);

  // Load chat history when opened
  useEffect(() => {
    if (isOpen && analysisId && token) {
      axios.get(`${API_URL}/api/chat/${analysisId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setMessages(res.data.messages || []);
        setMessagesUsed(res.data.messages_used || 0);
        setMessagesRemaining(res.data.messages_remaining ?? 5);
      }).catch(() => {});
    }
  }, [isOpen, analysisId, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => prev ? prev + ' ' + transcript : transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    setIsListening(true);
    recognition.start();
  }, []);

  const handleSend = async () => {
    if (!input.trim() || loading || !selectedLlm) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);

    // Optimistic update
    setMessages(prev => [...prev, { id: 'temp-user', role: 'user', content: userMessage, created_at: new Date().toISOString() }]);

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/chat`, {
        analysis_id: analysisId,
        message: userMessage,
        llm_id: selectedLlm,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(prev => [
        ...prev.filter(m => m.id !== 'temp-user'),
        { id: `user-${Date.now()}`, role: 'user', content: userMessage, created_at: new Date().toISOString() },
        { id: `assistant-${Date.now()}`, role: 'assistant', content: res.data.response, created_at: new Date().toISOString() },
      ]);
      setMessagesUsed(res.data.messages_used);
      setMessagesRemaining(res.data.messages_remaining);
      await refreshUser();
    } catch (err) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== 'temp-user'));
      if (err.response?.status === 402) {
        setMessagesRemaining(0);
      } else {
        setError(t('chat_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const needsCredit = messagesRemaining === 0 && messagesUsed > 0;
  const progressPercent = messagesRemaining > 0 ? (messagesRemaining / 5) * 100 : 0;

  return (
    <div className="max-w-3xl mx-auto mt-6">
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 rounded-2xl glass hover:bg-white/5 transition-all"
      >
        <div className="flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-emerald-400" />
          <span className="text-white font-medium">{t('chat_title')}</span>
          <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">{t('chat_first_msg_cost')}</span>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {isOpen && (
        <div className="mt-2 rounded-2xl glass overflow-hidden">
          {/* Context info */}
          <div className="px-4 py-2 bg-emerald-500/5 border-b border-white/5">
            <p className="text-xs text-slate-400">{t('chat_context')}</p>
          </div>

          {/* Messages area */}
          <div className="h-80 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-500 text-sm">{t('chat_ph')}</p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? 'bg-emerald-500/20 text-emerald-100 rounded-br-md'
                      : 'bg-slate-800/80 text-slate-200 rounded-bl-md'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-800/80 text-slate-400 px-4 py-2.5 rounded-2xl rounded-bl-md">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Progress bar + credits */}
          <div className="px-4 py-2 border-t border-white/5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-400">
                {t('chat_remaining', { n: messagesRemaining })}
              </span>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Coins className="w-3 h-3" />
                <span>{user?.credits || 0} {t('credits')}</span>
              </div>
            </div>
            <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Input area */}
          <div className="p-3 border-t border-white/5">
            {error && (
              <p className="text-xs text-rose-400 mb-2">{error}</p>
            )}

            {needsCredit ? (
              <button
                onClick={() => {
                  setMessagesRemaining(5);
                  // Next message will trigger credit deduction on backend
                }}
                disabled={user && user.credits < 1}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500/20 to-blue-500/20 border border-emerald-500/20 text-emerald-400 font-medium text-sm hover:border-emerald-500/40 transition-all disabled:opacity-50"
              >
                <div className="flex items-center justify-center gap-2">
                  <Coins className="w-4 h-4" />
                  {t('chat_continue')}
                </div>
              </button>
            ) : (
              <div className="flex items-center gap-2">
                {/* LLM selector mini */}
                {availableLlms && Object.keys(availableLlms).length > 1 && (
                  <select
                    value={selectedLlm}
                    onChange={(e) => setSelectedLlm(e.target.value)}
                    className="bg-slate-800 border border-white/10 text-white text-xs rounded-lg px-2 py-2 focus:outline-none focus:border-emerald-500/30"
                  >
                    {Object.entries(availableLlms).filter(([, info]) => info.available).map(([id, info]) => (
                      <option key={id} value={id}>{info.name}</option>
                    ))}
                  </select>
                )}

                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder={t('chat_ph')}
                    className="w-full bg-slate-800/60 border border-white/10 rounded-xl px-4 py-2.5 pr-10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/30"
                    disabled={loading}
                  />
                  <button
                    onClick={() => startListening()}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors ${
                      isListening ? 'text-rose-400' : 'text-slate-500 hover:text-white'
                    }`}
                  >
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                </div>

                <button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
